import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpProgressEvent,
} from '@angular/common/http';
import { AbstractUploadHandler } from './base-upload-handler';
import { MTMCustomFile } from '../mtm-custom-file';
import { TransferStatus } from '../transfer-status';
import { UrlSanitizer } from '../../api.service';
import { IS_UNCANCELABLE_REQUEST } from '../../../models';
import { listenerUploadProgressChanged } from '../listeners';
import { HttpHelperService } from '../../http-helper.service';

const uploadProgressThrottle: number = 2500;

@Injectable({
  providedIn: 'root',
})
export class DirectUploadHandler extends AbstractUploadHandler {
  constructor(
    private readonly http: HttpClient,
    private readonly httpHelperService: HttpHelperService,
    ) {
    super();
  }

  public canHandle(file: MTMCustomFile): boolean {
    return file.step === 'upload';
  }

  public handleUpload(file: MTMCustomFile): void {
    const signedUrl: string = file.dbFileObject.signedURL;
    const headers: Record<string, string> = {};
    const httpHeaders: HttpHeaders = this.httpHelperService.getHttpHeaders();

    httpHeaders.keys().forEach((name: string) => {
      const values: string[] = httpHeaders.getAll(name);
      if (values?.length && name && name !== 'Content-Type') {
        const [firstValue] = values;
        headers[name] = firstValue;
      }
    });

    headers['Content-Type'] = file.file.type;

    //don't encode the url
    const safeurl: string = UrlSanitizer.sanitizeUrl(signedUrl);

    file.currentRequest = this.http
      .put(safeurl, file.file, {
        headers: headers,
        reportProgress: true,
        observe: 'events',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
      })
      .subscribe({
        next: (response: HttpEvent<unknown>) => {
          this.handleUploadResponse(file, response);
        },
        error: (error: Error) => {
          this.handleError(file, error);
        },
      });
  }

  private handleUploadResponse(file: MTMCustomFile, response: HttpEvent<unknown>): void {
    switch (response.type) {
      case HttpEventType.UploadProgress:
        this.handleUploadProgress(file, response);
        break;
      case HttpEventType.Response:
        this.handleUploadComplete(file);
        break;
      case HttpEventType.Sent:
      case HttpEventType.ResponseHeader:
      case HttpEventType.DownloadProgress:
      case HttpEventType.User:
        break;
    }
  }

  private handleUploadProgress(file: MTMCustomFile, response: HttpProgressEvent): void {
    const curTimestamp: number = new Date().valueOf();
    if (curTimestamp - file.lastProgressTime < uploadProgressThrottle) {
      return;
    }

    let currentUploaded: number = file.startIndex + response.loaded;
    if (currentUploaded > file.fileSize) {
      currentUploaded = file.fileSize;
    }

    let newPercentage: number = Math.floor((currentUploaded * 100) / file.fileSize);
    if (newPercentage > 100) {
      newPercentage = 100;
    }

    file.extraInfo.totalUploaded = currentUploaded;

    if (newPercentage === file.progress) {
      return;
    }
    file.uploadStatusCode = TransferStatus.SENDING;
    const totalBytes: number = response.total ?? file.fileSize;
    const actualPercentage: number = (100 * response.loaded) / totalBytes;
    file.progress = Math.round(actualPercentage);
    if (file.progress > 0) {
      this.calculateRemainingTime(file, actualPercentage);
      listenerUploadProgressChanged.next(file);
    }
    file.lastProgressTime = curTimestamp;
  }

  private handleUploadComplete(file: MTMCustomFile): void {
    file.progress = 100;
    file.extraInfo.remainingTime = 0;
    file.uploadStatusCode = TransferStatus.UPLOADED;
    listenerUploadProgressChanged.next(file);
    this.emitProgress(file, {
      status: TransferStatus.UPLOADED,
      startIndex: file.fileSize,
      step: 'next',
    });
  }

  /**
   * Calculates estimated remaining upload time based on current progress
   * @param file - The file to calculate remaining time for
   * @param percentage - Current upload percentage
   */
  private calculateRemainingTime(file: MTMCustomFile, percentage: number): void {
    const elapsedTime: number = Date.now() - file.createTime;
    const safePercentage: number = percentage > 0 ? percentage : 1;
    const expectedDuration: number = (100 / safePercentage) * elapsedTime;
    file.extraInfo.remainingTime = expectedDuration - elapsedTime;
  }
}
